import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Airport Buddy</title>
        <meta name="description" content="Airport Buddy's terms of service. Read our terms and conditions for using our real-time airport information service." />
        <link rel="canonical" href="https://airportbuddy.app/terms" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://airportbuddy.app/terms" />
        <meta property="og:title" content="Terms of Service - Airport Buddy" />
        <meta property="og:description" content="The terms and conditions for using Airport Buddy's real-time airport wait time and travel information service." />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Terms of Service - Airport Buddy" />
        <meta name="twitter:description" content="The terms and conditions for using Airport Buddy's real-time airport information service." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="text-center space-y-4 mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                Terms of Service
              </h1>
              <p className="text-muted-foreground">
                Last updated: November 14, 2025
              </p>
            </header>

            <Card>
              <CardContent className="pt-6 space-y-6 prose prose-sm max-w-none">
                <section>
                  <h2 className="text-2xl font-semibold mb-3">1. Acceptance of Terms</h2>
                  <p className="text-muted-foreground">
                    By accessing or using Airport Buddy ("the Service"), you agree to be bound by these Terms of Service. 
                    If you do not agree to these terms, please do not use our service.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">2. Description of Service</h2>
                  <p className="text-muted-foreground">
                    Airport Buddy provides real-time information about airport wait times, flight statuses, and airport amenities. 
                    We strive to provide accurate information but cannot guarantee complete accuracy at all times.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">3. User Accounts</h2>
                  <h3 className="text-lg font-semibold mb-2">3.1 Account Creation</h3>
                  <p className="text-muted-foreground mb-4">
                    To access certain features, you must create an account. You agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your password</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Be responsible for all activities under your account</li>
                  </ul>

                  <h3 className="text-lg font-semibold mb-2">3.2 Account Termination</h3>
                  <p className="text-muted-foreground">
                    We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent, 
                    abusive, or illegal activities.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">4. User Conduct</h2>
                  <p className="text-muted-foreground mb-2">You agree not to:</p>
                  <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                    <li>Use the Service for any illegal purpose</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Interfere with the proper functioning of the Service</li>
                    <li>Impersonate any person or entity</li>
                    <li>Transmit viruses, malware, or harmful code</li>
                    <li>Scrape or data mine our content without permission</li>
                    <li>Violate any applicable laws or regulations</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">5. Intellectual Property</h2>
                  <p className="text-muted-foreground">
                    All content, features, and functionality of the Service are owned by Airport Buddy and protected by 
                    copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create 
                    derivative works without our express written permission.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">6. Disclaimers</h2>
                  <h3 className="text-lg font-semibold mb-2">6.1 Information Accuracy</h3>
                  <p className="text-muted-foreground mb-4">
                    While we strive to provide accurate real-time information, we cannot guarantee that all data is always 
                    current, complete, or error-free. Airport conditions change rapidly, and delays in data transmission may occur.
                  </p>

                  <h3 className="text-lg font-semibold mb-2">6.2 No Warranties</h3>
                  <p className="text-muted-foreground">
                    The Service is provided "as is" without warranties of any kind, either express or implied, including but not 
                    limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">7. Limitation of Liability</h2>
                  <p className="text-muted-foreground">
                    To the maximum extent permitted by law, Airport Buddy shall not be liable for any indirect, incidental, 
                    special, consequential, or punitive damages, including but not limited to missed flights, travel disruptions, 
                    or any other losses resulting from your use of or inability to use the Service.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">8. Indemnification</h2>
                  <p className="text-muted-foreground">
                    You agree to indemnify and hold harmless Airport Buddy, its affiliates, and their respective officers, 
                    directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising from 
                    your use of the Service or violation of these Terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">9. Third-Party Links</h2>
                  <p className="text-muted-foreground">
                    Our Service may contain links to third-party websites or services. We are not responsible for the content, 
                    privacy policies, or practices of third-party sites.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">10. Changes to Terms</h2>
                  <p className="text-muted-foreground">
                    We reserve the right to modify these Terms at any time. We will notify users of significant changes by 
                    posting the updated Terms on this page. Continued use of the Service after changes constitutes acceptance 
                    of the new Terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">11. Governing Law</h2>
                  <p className="text-muted-foreground">
                    These Terms shall be governed by and construed in accordance with the laws of the United States, without 
                    regard to its conflict of law provisions.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-semibold mb-3">12. Contact Information</h2>
                  <p className="text-muted-foreground">
                    If you have questions about these Terms, please contact us at:
                  </p>
                  <p className="text-muted-foreground mt-2">
                    Email: <a href="mailto:legal@airportbuddy.app" className="text-primary hover:underline">legal@airportbuddy.app</a>
                  </p>
                </section>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
